/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import './style/vatDeclaration.scss';

import {AwesomeIcon, Checkbox, IndexedObject, Link, Loader} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {Fragment, MouseEventHandler, ReactElement, useContext, useMemo} from 'react';
import VatDeclarationItem from "../../models/vatDeclarationItemModel";
import {
    AccessKey, BolInvoice, BolInvoiceItemType, CategoryType, ExportProcessStatus, ProcessingStatus, TaxTotalsData,
    VatDeclarationInvalidation, VatPercentageData
} from "../../util/interfaces";
import {calculateRevenues, calculateTaxes, getBolInvoices, groupVatItems} from "../../util/vatDeclarationUtil";
import {parseMoney} from "../../util/utils";
import ProcessStatus from "../../models/processStatusModel";
import CountryFlag from "../misc/countryFlag";
import VatPeriodSelectController from "./vatPeriodSelect/vatPeriodSelectController";
import {BolmateButton} from "../controls";
import InfoIcon from "../icon/infoIcon";
import ExportProcess from "../../models/exportProcessModel";
import {AppContext} from "../misc/appContext";
import WarningMessage from "../misc/messages/warningMessage";
import {DUTCH_DATE_FORMAT} from "../../util/constants";
import useRight from "../misc/useRight";
import InfoBanner from "../misc/infoBanner";

interface Props {
    onFetch: MouseEventHandler;
    onChangePeriod: (start: IDate, end: IDate, isCustom?: boolean) => void;
    declarationItems: IndexedObject<VatDeclarationItem[]>;
    onCloseInfo: MouseEventHandler;
    onDownloadInvoice: (invoiceId: string, fileType: 'pdf' | 'excel') => MouseEventHandler;
    onRecalculate: MouseEventHandler;
    onNavigateToProducts: MouseEventHandler;
    onToggleMail: MouseEventHandler;
    onDownloadExport: (exportProcess: ExportProcess) => MouseEventHandler;
    onExportDeclaration: MouseEventHandler;
    period: {start: IDate, end: IDate} | null;
    loading: boolean;
    loadingExport: boolean
    invalidated: VatDeclarationInvalidation | null;
    processStatus: ProcessStatus | null;
    showInfo: boolean;
    defaultVats: IndexedObject<number>;
    exportProcesses: Array<ExportProcess>;
}

const preTaxInfo = 'De voorbelasting is de BTW die je zelf hebt betaald over de gemaakte kosten bij Bol. Deze ' +
    'BTW krijg je van de Belastingdienst terug via de BTW-aangifte.';
const taxInfo = 'De af te dragen BTW is de BTW die je ontvangen hebt van jouw klanten. Deze BTW draag je af aan ' +
    'de Belastingdienst via de BTW-aangifte.';
const reversedInfo = 'Als je bestellingen van buitenlandse zakelijke klanten ontvangt, dan kan het zijn dat je de ' +
    'BTW hebt verlegd naar de klant. Tijdens de BTW aangifte moet je de omzet opgeven waarvan de BTW verlegd is.'
const mailKey = 'notify_vat_declaration_finished';

function sortItems(items: Array<VatDeclarationItem>) {
    items.sort((itemA: VatDeclarationItem, itemB: VatDeclarationItem) => {
        if ([BolInvoiceItemType.TURNOVER, BolInvoiceItemType.TURNOVER_BOL].includes(itemA.category)) return -1;
        else if ([BolInvoiceItemType.TURNOVER, BolInvoiceItemType.TURNOVER_BOL].includes(itemB.category)) return 1;
        return itemA.categoryTranslation.localeCompare(itemB.categoryTranslation);
    })
}

export default function VatDeclaration(props: Props) {
    const context = useContext(AppContext);
    const [canExport, canDownload] = useRight(AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC);
    const {
        declarationItems, period, onFetch, loading, loadingExport, invalidated, processStatus, onChangePeriod,
        onCloseInfo, showInfo, onDownloadInvoice, onRecalculate, onNavigateToProducts, defaultVats, onToggleMail,
        onExportDeclaration, onDownloadExport, exportProcesses
    } = props;
    const [revenueItems, costItems, lastOrderDate] = useMemo(() => {
        const groupedItems: VatDeclarationItem[] = groupVatItems(Object.assign({}, declarationItems));
        const _costItems: VatDeclarationItem[] = [];
        const _revenueItems: VatDeclarationItem[] = [];
        let lastOrderDate: IDate | null = null;
        for (const item of groupedItems) {
            if (item.lastOrderDate) {
                if (!lastOrderDate) lastOrderDate = item.lastOrderDate;
                else if (lastOrderDate.isBefore(item.lastOrderDate)) lastOrderDate = item.lastOrderDate;
            }
            if (item.categoryType === CategoryType.COSTS) _costItems.push(item);
            else _revenueItems.push(item);
        }
        sortItems(_revenueItems);
        sortItems(_costItems);
        return [_revenueItems, _costItems, lastOrderDate];
    }, [declarationItems]);
    const showReverseDisclaimer = useMemo(() => {
        return context.accountContext.account?.country !== 'Nederland';
    }, [context.accountContext.account]);

    function determineInvoicePeriod(bolInvoice: BolInvoice): [IDate, IDate] {
        let periodStart = new IDate(bolInvoice.period_start);
        let periodEnd = new IDate(bolInvoice.period_end);
        if (periodStart.isBefore(period!.start, 'd')) periodStart = period!.start;
        if (periodEnd.isAfter(period!.end, 'd')) periodEnd = period!.end;
        return [periodStart, periodEnd];
    }

    function renderInfo() {
        return (
            <InfoBanner className='general-info-container' onClose={onCloseInfo}
                message='We berekenen deze cijfers aan de hand van jouw maandelijkse factuurspecificaties van Bol. Deze vullen wij nog aan met de BTW percentages van jouw producten, zoals ingesteld in Bolmate en met eventuele verlegd BTW via Bolmate. Zorg dus dat de BTW percentages van producten in Bolmate goed zijn ingesteld.'
                persistDismiss={false}
                variant={'warning'}
            />
        )
    }

    function renderReverseDisclaimer() {
        return (
            <WarningMessage>
                Voor verkooppartners buiten Nederland wordt de BTW van de kosten door Bol verlegd. In de
                factuurspecificatie van Bol wordt dit niet duidelijk aangegeven, hierdoor kunnen wij de BTW
                bedragen van de kosten (voorbelasting) niet goed berekenen. Wij raden aan een boekhouder/accountant te
                raadplegen voor het doen van je BTW-aangifte.
            </WarningMessage>
        )
    }

    function renderLastOrderDate() {
        const today = new IDate();
        const prevMonth = new IDate().subtract(1, 'month');
        const calculatingPrevPeriod = period?.end.isAt(prevMonth, 'month');
        const availableFrom = new IDate().day(9);
        if (today.isAtOrAfter(availableFrom, 'day') || !calculatingPrevPeriod || !lastOrderDate) return null;
        const dateFormat = "D MMMM YYYY";
        const lastOrderString = lastOrderDate.format(dateFormat);
        const availableFromString = availableFrom.format(dateFormat);
        return (
            <WarningMessage>
                <div className='last-order-date-info'>
                    <label className='info-title'>Data van bol ontvangen tot {lastOrderString} – Volledige gegevens
                        waarschijnlijk beschikbaar vanaf {availableFromString}</label>
                    <div className='info-container'>
                        <InfoIcon/>
                        <p className='info-message'>
                            Voor deze berekening gebruiken we dezelfde factuurspecificaties van bol in Excel die jij ook
                            ontvangt. We vullen deze aan met de BTW-gegevens van Bolmate. De data die we hebben
                            opgehaald bevat orders tot en met {lastOrderString}. Dit kan twee dingen betekenen:
                            <ol className='options-list'>
                                <li><b>Jouw berekening is nu compleet</b> als dit de laatste order van de maand was.
                                </li>
                                <li><b>Jouw berekening is nu nog niet compleet</b> als er na {lastOrderString} nog
                                    orders waren. In dat geval komt rond {availableFromString} een nieuwe Excel-sheet
                                    beschikbaar bij bol. Voer deze berekening vanaf {availableFromString} opnieuw uit.
                                </li>
                            </ol>
                        </p>
                    </div>
                </div>
            </WarningMessage>
        )
    }

    function renderPeriod() {
        const dateFormat = 'D MMMM YYYY';
        const isLoading = processStatus?.isActive() === true;
        return (
            <>
                <div className='period-row-container'>
                    <div className={classNames('period-container', isLoading && 'loading')}>
                        {period === null ? (
                            <label className='period grid-col-3'>Kies een periode</label>
                        ) : (
                            <>
                                <label className='period'>{period.start.format(dateFormat)}</label>
                                <label className='tm'>t/m</label>
                                <label className='period'>{period.end.format(dateFormat)}</label>
                            </>
                        )}
                    </div>
                    {renderRefresh()}
                </div>
                {isLoading && (
                    <div className={classNames('period-container', isLoading && 'loading')}>
                        {renderLoadingPeriod()}
                    </div>
                )}
            </>
        )
    }

    function renderRefresh() {
        if (!period) return null;
        return (
            <div className={classNames('btn-refresh', loading && 'disabled')}
                 onClick={loading ? undefined : onRecalculate}>
                <AwesomeIcon name='refresh' className='refresh-icon'/>
                <label className='btn-label'>Opnieuw berekenen</label>
            </div>
        );
    }

    function renderLoadingPeriod() {
        const {progress, data} = processStatus!;
        const periodStart = new IDate(data.period_start).format('MMMM YYYY');
        const periodEnd = new IDate(data.period_end).format('MMMM YYYY');
        const periodLabel = periodStart === periodEnd ? periodStart : `${periodStart} t/m ${periodEnd}`;
        const {file_index} = data;
        const loader = (
            <div className='dots-loader'>
                <div className='dot-pulse'/>
            </div>
        );
        const mailCheck = <Checkbox checked={!!data[mailKey]} onCheck={onToggleMail}
                                    label='Stuur mij een mail zodra de berekening voltooid is'/>
        if (file_index) return (
            <div className='period-loader'>
                {loader}
                <label className='period-label'>Bezig met het verwerken van periode {periodLabel}</label>
                <label>Status: {progress}</label>
                <label>Je kunt dit venster verlaten en later terugkomen.</label>
                {mailCheck}
            </div>
        )
        else return (
            <div className='period-loader'>
                {loader}
                <label className='period-label'>Bezig met het verwerken van periode {periodLabel}</label>
                <label>Status: {progress}</label>
                <label>Je kunt dit venster verlaten en later terugkomen.</label>
                {mailCheck}
            </div>
        )
    }

    function renderGeneralWarnings() {
        return (
            <>
                {invalidated !== null && <div className='general-warning'>
                    <AwesomeIcon name='triangle-exclamation' className='warn-icon'/>
                    <label className='warning'>Sinds de laatste berekening van de BTW aangifte zijn
                        de {invalidated === VatDeclarationInvalidation.API ? 'API' : 'BTW'} instellingen
                        gewijzigd. Hierdoor moet de berekening mogelijk opnieuw gedaan worden. <Link disabled={loading}
                                                                                                     onClick={onFetch}>Nu
                            opnieuw berekenen</Link></label>
                </div>}
            </>
        )
    }

    function renderTotals() {
        const taxesPerCountry = calculateTaxes(revenueItems, costItems);
        const revenues = calculateRevenues(revenueItems);
        const costs = calculateRevenues(costItems);
        return (
            <div className='totals-container'>
                <label className='part-label'>Totalen</label>
                <div className='totals'>
                    {renderTaxBox('revenueIncl', revenues)}
                    {renderTaxBox('revenueExcl', revenues)}
                    {renderTaxBox('tax', taxesPerCountry, taxInfo)}
                    {renderTaxBox('reversedIncl', revenues, reversedInfo)}
                    {renderTaxBox('revenueIncl', costs, undefined, true)}
                    {renderTaxBox('revenueExcl', costs, undefined, true)}
                    {renderTaxBox('preTax', taxesPerCountry, preTaxInfo)}
                </div>
            </div>
        )
    }

    function renderTaxBox(valueKey: keyof TaxTotalsData, taxesPerCountry: IndexedObject<TaxTotalsData>, info?: string,
                          isCosts?: boolean) {
        let title: string;
        if (valueKey === 'tax')
            title = 'Af te dragen BTW';
        else if (valueKey === 'preTax')
            title = 'Voorbelasting';
        else if (valueKey === 'revenueIncl')
            title = isCosts ? 'Kosten incl. BTW' : 'Omzet incl. BTW';
        else if (valueKey === 'reversedIncl')
            title = 'Omzet met verlegde BTW';
        else
            title = isCosts ? 'Kosten excl. BTW' : 'Omzet excl. BTW'
        const loadLabel = 'Wordt berekend...';
        const noDataLabel = 'Geen beschikbare data gevonden';
        const countryCodes = Object.keys(taxesPerCountry);
        let hasData: boolean = false;
        for (const countryCode of countryCodes) {
            const vatPercentages = Object.keys(taxesPerCountry[countryCode][valueKey]);
            if (!vatPercentages.length) continue;
            for (const vatPercentage of vatPercentages) {
                if (taxesPerCountry[countryCode][valueKey][vatPercentage] > 0) {
                    hasData = true;
                    break;
                }
            }
        }
        if (!hasData && valueKey === 'reversedIncl') return null;
        return (
            <div className='tax-box'>
                <div className='box-header'>
                    <label className='box-label'>{title}</label>
                    {!!info && (
                        <div className='decl-info-container'>
                            <InfoIcon/>
                            <p className='info-text'>
                                {info}
                            </p>
                        </div>
                    )}
                </div>
                <div className='box-content'>
                    {loading ? <label className='load-label'>{loadLabel}</label> :
                        hasData ? renderTotalRows(taxesPerCountry, valueKey) :
                            <label className='load-label'>{noDataLabel}</label>

                    }
                </div>
            </div>
        )
    }

    function renderTotalRows(taxesPerCountry: IndexedObject<TaxTotalsData>,
                             dataKey: keyof TaxTotalsData): Array<ReactElement> {
        const rows: Array<ReactElement> = [];
        const showVat = dataKey !== 'reversedIncl';
        for (const [countryCode, vatValues] of Object.entries(taxesPerCountry)) {
            const taxTotals = vatValues[dataKey];
            Object.entries(taxTotals).forEach(([vatPercentage, value]: [string, number], index: number) => {
                if (!value) return;
                rows.push(
                    <Fragment key={`${countryCode}-${index}`}>
                        <label className='country'><CountryFlag
                            countryCode={countryCode}/>{showVat ? `${vatPercentage}% BTW` : ''}</label>
                        <label className='amount'>&euro;{parseMoney(value)}</label>
                    </Fragment>
                )
            })
        }
        return rows;
    }

    function renderLists() {
        return (
            <>
                {renderList('Omzet bij Bol', revenueItems)}
                {renderList('Gemaakte kosten bij Bol', costItems)}
            </>
        )

    }

    function renderList(title: string, items: VatDeclarationItem[]) {
        return (
            <div className='declaration-list'>
                <label className='list-title'>{title}</label>
                <div className='items-table'>
                    <div className='row head'>
                        <label>Categorie</label>
                        <label/>
                        <label>Bedrag excl. BTW</label>
                        <label>BTW bedrag</label>
                        <label>Totaal</label>
                    </div>
                    {items.map(renderListItem)}
                </div>
            </div>
        )
    }

    function renderListItem(item: VatDeclarationItem, index: number) {
        return (
            <Fragment key={index}>
                <div className='row bold'>
                    <label>{item.categoryTranslation}</label>
                    <label>Totaal</label>
                    <label>&euro;{parseMoney(item.getTotalExclVat())}</label>
                    <label>&euro;{parseMoney(item.getTotalVat())}</label>
                    <label>&euro;{parseMoney(item.getTotalInclVat())}</label>
                </div>
                {renderItemData(item)}
            </Fragment>
        )
    }

    function renderItemData(item: VatDeclarationItem): Array<ReactElement> {
        const countryCodes = Array.from(item.getCountryCodes());
        const rows: Array<ReactElement> = [];
        for (const countryCode of countryCodes) {
            Object.entries(item.data[countryCode])
                .forEach(([vatPercentage, values]: [string, VatPercentageData], index: number) => {
                    if (!values.total_incl_vat && !values.total_vat && !values.total_incl_reversed_vat) return;
                    if (values.total_incl_vat) {
                        const totalExclVat = values.total_incl_vat - values.total_vat;
                        rows.push(
                            <div key={`${countryCode}-${index}`} className='row'>
                                <label/>
                                <label className='country'><CountryFlag countryCode={countryCode}/>
                                    <span>{vatPercentage}% BTW</span></label>
                                <label>&euro;{parseMoney(totalExclVat)}</label>
                                <label>&euro;{parseMoney(values.total_vat)}</label>
                                <label>&euro;{parseMoney(values.total_incl_vat)}</label>
                            </div>
                        );
                    }
                    if (values.total_incl_reversed_vat) {
                        rows.push(
                            <div key={`${countryCode}-${index}`} className='row'>
                                <label/>
                                <label className='country'><CountryFlag countryCode={countryCode}/>
                                    <span>BTW verlegd</span></label>
                                <label>&euro;{parseMoney(values.total_incl_reversed_vat)}</label>
                                <label>&euro;{parseMoney(0)}</label>
                                <label>&euro;{parseMoney(values.total_incl_reversed_vat)}</label>
                            </div>
                        );
                    }
                });
        }
        return rows;
    }

    function renderProcessStatus() {
        if (processStatus?.status !== ProcessingStatus.WAITING) return null;
        return renderWaitingProcess();
    }

    function renderWaitingProcess() {
        const missingProducts = processStatus!.data['missing_products'];
        const dutchVat = defaultVats['NL'] ?? 21;
        const beVat = defaultVats['BE'] ?? 21;
        return (
            <div className='process-status-background'>
                <div className='process-status waiting'>
                    <div className='header-container'>
                        <label className='header-title'>Nieuwe producten gevonden!</label>
                    </div>
                    <div className='body-container'>
                        <p>Ons systeem heeft tijdens de berekening van de BTW aangifte {missingProducts} nieuwe
                            producten gevonden die we nog niet eerder hebben gezien. Dit zijn waarschijnlijk producten
                            van die periode destijds, die je nu niet meer actief verkoopt. Deze producten hebben we
                            toegevoegd in Instellingen {'>'} Producten (BTW).
                            <br/><br/><b>Van deze producten weten we het BTW tarief nog niet.
                                <br/>Voor deze producten wordt momenteel het standaard BTW tarief per land
                                (Nederland {dutchVat}%, België {beVat}%) gehanteerd.</b></p>
                        <br/>
                        <p>Wil je andere BTW tarieven instellen of wil je doorgaan met het standaard BTW tarief?</p>
                    </div>
                    <div className='footer-container'>
                        <BolmateButton onClick={onNavigateToProducts} label='Ander BTW tarief instellen' secondary/>
                        <BolmateButton onClick={onRecalculate} label='Doorgaan met standaard BTW tarief'/>
                    </div>
                </div>
            </div>
        )
    }

    function renderBolInvoices() {
        const invoices = getBolInvoices(declarationItems);
        return (
            <div className='bol-invoices'>
                <label className='part-label'>Downloads</label>
                {renderExportProcesses()}
                <div className='invoices-container'>
                    <label className='invoices-title'>Gebruikte facturen & specificaties van Bol</label>
                    <div className='invoices-list'>
                        <div className='row head'>
                            <label>PERIODE</label>
                            <label>FACTUURNUMMER</label>
                            <div className='decl-info-container'>
                                <InfoIcon/>
                                <p className='info-text'>
                                    Download hier de originele Bol facturen (PDF) of factuurspecificaties (Excel).
                                </p>
                            </div>
                        </div>
                        {invoices.map(renderBolInvoice)}
                        <Loader size='4rem' active={loadingExport}/>
                    </div>
                </div>
            </div>
        )
    }

    function renderExportProcesses() {
        return (
            <div className='exports-container'>
                <div className='export-header'>
                    <label className='invoices-title'>BTW berekening exporteren</label>
                    <BolmateButton onClick={onExportDeclaration} label='Berekening exporteren'
                                   disabled={loading || !period || !canExport}/>
                </div>
                <div className='export-processes'>
                    <div className='row default-table-row head'>
                        <label>BESTANDSTYPE</label>
                        <label>ACTIE</label>
                    </div>
                    {exportProcesses.map(renderExportProcess)}
                    {!exportProcesses.length && (
                        <div className='default-table-row empty'>
                            <label className='empty-label'>- nog geen berekeningen geëxporteerd -</label>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    function renderExportProcess(exportProcess: ExportProcess) {
        return (
            <div key={exportProcess.id} className='row default-table-row'>
                <label>{exportProcess.translatedExportType}</label>
                {renderProcessAction(exportProcess)}
            </div>
        )
    }

    function renderProcessAction(exportProcess: ExportProcess) {
        const {status} = exportProcess;
        if (status === ExportProcessStatus.EXPORTING)
            return <label className='exporting'><Loader className='status-loader' size='1rem' thickness='.2rem'
                                                        active disableBackground/> Bezig met exporteren...</label>;
        else if (status === ExportProcessStatus.ERROR)
            return <label className='error'>Fout</label>;
        else
            return <label className={classNames('download', !canExport && 'cursor-default')}
                          onClick={canExport ? onDownloadExport(exportProcess) : undefined}>Downloaden</label>
    }

    function renderBolInvoice(bolInvoice: BolInvoice, index: number) {
        const isDemo = context.userContext.user?.isDemoAccount === true;
        const [periodStart, periodEnd] = determineInvoicePeriod(bolInvoice);
        const period = periodStart.format(DUTCH_DATE_FORMAT) + ' t/m ' + periodEnd.format(DUTCH_DATE_FORMAT);
        return (
            <div className='row' key={index}>
                <label>{period}</label>
                <label>{bolInvoice.invoice_id}</label>
                <div className='download-actions'>
                    {!isDemo && <AwesomeIcon name='file-pdf'
                                             className={classNames('download-icon pdf',
                                                 !canDownload && 'disabled cursor-default')}
                                             onClick={canDownload ? onDownloadInvoice(bolInvoice.invoice_id, 'pdf') :
                                                 undefined}
                                             title='Download PDF factuur'/>}
                    <AwesomeIcon name='file-excel'
                                 className={classNames('download-icon excel',
                                     !canDownload && 'disabled cursor-default')}
                                 onClick={canDownload ? onDownloadInvoice(bolInvoice.invoice_id, 'excel') : undefined}
                                 title='Download Excel specificatie'/>
                </div>
            </div>
        )
    }

    return (
        <>
            <InfoBanner featureKey='vat_declaration' variant='info'
                        title='BTW-aangifte'
                        message='Bereken in één klik je btw bedragen over je bol verkopen. Let op: laat controleren door jouw boekhouder.'
                        showForNewUsersOnly={true}/>
            <div className='vat-declaration'>
                {showInfo && renderInfo()}
                <VatPeriodSelectController onChange={onChangePeriod} loading={loading}/>
                {renderPeriod()}
                {showReverseDisclaimer && renderReverseDisclaimer()}
                {renderLastOrderDate()}
                {renderProcessStatus()}
                {renderGeneralWarnings()}
                <div className='result-container'>
                    {renderTotals()}
                    {renderLists()}
                    {renderBolInvoices()}
                </div>
            </div>
        </>
    )
}